import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Filter,
  X,
  SlidersHorizontal,
  Car,
  Search
} from 'lucide-react';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import VehicleCard from './VehicleCard';
import { useVehicles } from '@/contexts/VehicleContext';
import { GitCompare, Heart } from 'lucide-react';
import ScrollToTop from './ScrollToTop';
import WhatsAppButton from './WhatsAppButton';
import SEOHead from './SEOHead';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const FilterSection = ({ filters, selectedBrand, setSelectedBrand, selectedModel, setSelectedModel, selectedBodyType, setSelectedBodyType, selectedFuelType, setSelectedFuelType, selectedTransmission, setSelectedTransmission, yearMin, setYearMin, yearMax, setYearMax, priceMin, setPriceMin, priceMax, setPriceMax, mileageMax, setMileageMax, searchQuery, setSearchQuery, resetFilters, activeFiltersCount }) => (
  <div className="space-y-6">
    {/* Search */}
    <div>
      <label className="text-sm font-semibold mb-2 block">Search</label>
      <Input
        placeholder="Search vehicles..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
    </div>

    {/* Brand */}
    <div>
      <label className="text-sm font-semibold mb-2 block">Brand</label>
      <Select value={selectedBrand || "all"} onValueChange={(val) => setSelectedBrand(val === "all" ? "" : val)}>
        <SelectTrigger>
          <SelectValue placeholder="All Brands" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Brands</SelectItem>
          {filters?.brands?.map((brand) => (
            <SelectItem key={brand} value={brand}>{brand}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Model */}
    <div>
      <label className="text-sm font-semibold mb-2 block">Model</label>
      <Select value={selectedModel || "all"} onValueChange={(val) => setSelectedModel(val === "all" ? "" : val)}>
        <SelectTrigger>
          <SelectValue placeholder="All Models" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Models</SelectItem>
          {filters?.models?.map((model) => (
            <SelectItem key={model} value={model}>{model}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Year Range */}
    <div>
      <label className="text-sm font-semibold mb-2 block">Year</label>
      <div className="grid grid-cols-2 gap-2">
        <Input
          type="number"
          placeholder="Min"
          value={yearMin}
          onChange={(e) => setYearMin(e.target.value)}
        />
        <Input
          type="number"
          placeholder="Max"
          value={yearMax}
          onChange={(e) => setYearMax(e.target.value)}
        />
      </div>
    </div>

    {/* Price Range */}
    <div>
      <label className="text-sm font-semibold mb-2 block">
        Price (USD)
      </label>
      <div className="grid grid-cols-2 gap-2">
        <Input
          type="number"
          placeholder="Min"
          value={priceMin}
          onChange={(e) => setPriceMin(e.target.value)}
        />
        <Input
          type="number"
          placeholder="Max"
          value={priceMax}
          onChange={(e) => setPriceMax(e.target.value)}
        />
      </div>
    </div>

    {/* Body Type */}
    <div>
      <label className="text-sm font-semibold mb-2 block">Body Type</label>
      <Select value={selectedBodyType || "all"} onValueChange={(val) => setSelectedBodyType(val === "all" ? "" : val)}>
        <SelectTrigger>
          <SelectValue placeholder="All Types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Types</SelectItem>
          {filters?.body_types?.map((type) => (
            <SelectItem key={type} value={type}>{type}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Fuel Type */}
    <div>
      <label className="text-sm font-semibold mb-2 block">Fuel Type</label>
      <Select value={selectedFuelType || "all"} onValueChange={(val) => setSelectedFuelType(val === "all" ? "" : val)}>
        <SelectTrigger>
          <SelectValue placeholder="All Fuel Types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Fuel Types</SelectItem>
          {filters?.fuel_types?.map((type) => (
            <SelectItem key={type} value={type}>{type}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Transmission */}
    <div>
      <label className="text-sm font-semibold mb-2 block">Transmission</label>
      <Select value={selectedTransmission || "all"} onValueChange={(val) => setSelectedTransmission(val === "all" ? "" : val)}>
        <SelectTrigger>
          <SelectValue placeholder="All Transmissions" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Transmissions</SelectItem>
          {filters?.transmissions?.map((type) => (
            <SelectItem key={type} value={type}>{type}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Mileage */}
    <div>
      <label className="text-sm font-semibold mb-2 block">Max Mileage (km)</label>
      <Input
        type="number"
        placeholder="e.g., 100000"
        value={mileageMax}
        onChange={(e) => setMileageMax(e.target.value)}
      />
    </div>

    {/* Reset Button */}
    <Button 
      variant="outline" 
      className="w-full"
      onClick={resetFilters}
    >
      <X className="h-4 w-4 mr-2" />
      Reset Filters
    </Button>
  </div>
);

const VehicleListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { compareList, favorites } = useVehicles();
  
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Filter states
  const [selectedBrand, setSelectedBrand] = useState(searchParams.get('brand') || '');
  const [selectedModel, setSelectedModel] = useState(searchParams.get('model') || '');
  const [selectedBodyType, setSelectedBodyType] = useState('');
  const [selectedFuelType, setSelectedFuelType] = useState('');
  const [selectedTransmission, setSelectedTransmission] = useState('');
  const [selectedDriveType, setSelectedDriveType] = useState('');
  const [yearMin, setYearMin] = useState('');
  const [yearMax, setYearMax] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [mileageMax, setMileageMax] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sorting
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    fetchFilters();
  }, []);

  // Debounced search to avoid excessive API calls
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchVehicles();
  }, [selectedBrand, selectedModel, selectedBodyType, selectedFuelType, 
      selectedTransmission, yearMin, yearMax, priceMin, priceMax, mileageMax, 
      debouncedSearch, sortBy, sortOrder]);

  const fetchFilters = async () => {
    try {
      const response = await axios.get(`${API}/public/filters`);
      setFilters(response.data);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      if (initialLoad) {
        setLoading(true);
      }
      const params = new URLSearchParams();
      
      if (selectedBrand) params.append('brand', selectedBrand);
      if (selectedModel) params.append('model', selectedModel);
      if (selectedBodyType) params.append('body_type', selectedBodyType);
      if (selectedFuelType) params.append('fuel_type', selectedFuelType);
      if (selectedTransmission) params.append('transmission', selectedTransmission);
      if (yearMin) params.append('year_min', yearMin);
      if (yearMax) params.append('year_max', yearMax);
      if (priceMin) params.append('price_min', priceMin);
      if (priceMax) params.append('price_max', priceMax);
      if (mileageMax) params.append('mileage_max', mileageMax);
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      params.append('limit', '100');
      
      const response = await axios.get(`${API}/public/vehicles?${params.toString()}`);
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const resetFilters = () => {
    setSelectedBrand('');
    setSelectedModel('');
    setSelectedBodyType('');
    setSelectedFuelType('');
    setSelectedTransmission('');
    setYearMin('');
    setYearMax('');
    setPriceMin('');
    setPriceMax('');
    setMileageMax('');
    setSearchQuery('');
    setSearchParams({});
  };

  const activeFiltersCount = [
    selectedBrand, selectedModel, selectedBodyType, selectedFuelType,
    selectedTransmission, yearMin, yearMax, priceMin, priceMax, mileageMax
  ].filter(Boolean).length;

  const seoTitleParts = [
    'Browse Japanese Used Cars',
    selectedBrand || null,
    selectedModel || null,
    '- Global Trade Car'
  ].filter(Boolean);

  const seoDescription = selectedBrand || selectedModel
    ? `Browse Japanese used cars${selectedBrand ? ` by ${selectedBrand}` : ''}${selectedModel ? ` ${selectedModel}` : ''}. Filter by year, price, mileage and more. Worldwide export and shipping available.`
    : 'Browse our inventory of export-quality Japanese used cars. Filter by brand, model, year, price and more. Worldwide shipping available.';

  const listStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: seoTitleParts.join(' '),
    description: seoDescription,
    numberOfItems: vehicles.length,
    itemListElement: vehicles.slice(0, 20).map((v, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `https://globaltradecar.com/vehicles/${v.id}`,
      name: `${v.year} ${v.brand || ''} ${v.model}`.trim()
    }))
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={seoTitleParts.join(' ')}
        description={seoDescription}
        keywords="Japanese used cars, car export Japan, browse vehicles, Toyota, Honda, Nissan, JDM vehicles, right hand drive cars"
        structuredData={listStructuredData}
      />
      <PublicHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Browse Vehicles</h1>
          <p className="text-gray-600">
            {loading ? 'Loading...' : `${vehicles.length} vehicles available`}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Desktop Filters Sidebar */}
          <div className="hidden lg:block">
            <Card className="sticky top-20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center">
                    <Filter className="h-5 w-5 mr-2" />
                    Filters
                  </h2>
                  {activeFiltersCount > 0 && (
                    <Badge>{activeFiltersCount}</Badge>
                  )}
                </div>
                <FilterSection
                  filters={filters}
                  selectedBrand={selectedBrand} setSelectedBrand={setSelectedBrand}
                  selectedModel={selectedModel} setSelectedModel={setSelectedModel}
                  selectedBodyType={selectedBodyType} setSelectedBodyType={setSelectedBodyType}
                  selectedFuelType={selectedFuelType} setSelectedFuelType={setSelectedFuelType}
                  selectedTransmission={selectedTransmission} setSelectedTransmission={setSelectedTransmission}
                  yearMin={yearMin} setYearMin={setYearMin}
                  yearMax={yearMax} setYearMax={setYearMax}
                  priceMin={priceMin} setPriceMin={setPriceMin}
                  priceMax={priceMax} setPriceMax={setPriceMax}
                  mileageMax={mileageMax} setMileageMax={setMileageMax}
                  searchQuery={searchQuery} setSearchQuery={setSearchQuery}
                  resetFilters={resetFilters}
                  activeFiltersCount={activeFiltersCount}
                />
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Mobile Filters & Sorting Bar */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  {/* Mobile Filter Button */}
                  <Button
                    variant="outline"
                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                    className="lg:hidden"
                  >
                    <SlidersHorizontal className="h-4 w-4 mr-2" />
                    Filters
                    {activeFiltersCount > 0 && (
                      <Badge className="ml-2">{activeFiltersCount}</Badge>
                    )}
                  </Button>

                  {/* Sort Dropdown */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 hidden sm:inline">Sort by:</span>
                    <Select 
                      value={`${sortBy}-${sortOrder}`} 
                      onValueChange={(value) => {
                        const [newSortBy, newSortOrder] = value.split('-');
                        setSortBy(newSortBy);
                        setSortOrder(newSortOrder);
                      }}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="created_at-desc">Newest First</SelectItem>
                        <SelectItem value="created_at-asc">Oldest First</SelectItem>
                        <SelectItem value="public_price_usd-asc">Price: Low to High</SelectItem>
                        <SelectItem value="public_price_usd-desc">Price: High to Low</SelectItem>
                        <SelectItem value="year-desc">Year: Newest</SelectItem>
                        <SelectItem value="year-asc">Year: Oldest</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Mobile Filters Panel */}
                {showMobileFilters && (
                  <div className="mt-4 pt-4 border-t lg:hidden">
                <FilterSection
                  filters={filters}
                  selectedBrand={selectedBrand} setSelectedBrand={setSelectedBrand}
                  selectedModel={selectedModel} setSelectedModel={setSelectedModel}
                  selectedBodyType={selectedBodyType} setSelectedBodyType={setSelectedBodyType}
                  selectedFuelType={selectedFuelType} setSelectedFuelType={setSelectedFuelType}
                  selectedTransmission={selectedTransmission} setSelectedTransmission={setSelectedTransmission}
                  yearMin={yearMin} setYearMin={setYearMin}
                  yearMax={yearMax} setYearMax={setYearMax}
                  priceMin={priceMin} setPriceMin={setPriceMin}
                  priceMax={priceMax} setPriceMax={setPriceMax}
                  mileageMax={mileageMax} setMileageMax={setMileageMax}
                  searchQuery={searchQuery} setSearchQuery={setSearchQuery}
                  resetFilters={resetFilters}
                  activeFiltersCount={activeFiltersCount}
                />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Vehicles Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <div className="h-48 bg-gray-200"></div>
                    <CardContent className="p-6">
                      <div className="h-6 bg-gray-200 rounded mb-4"></div>
                      <div className="h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : vehicles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {vehicles.map((vehicle) => (
                  <VehicleCard 
                    key={vehicle.id} 
                    vehicle={vehicle} 
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Car className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    No Vehicles Found
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Try adjusting your filters or search criteria
                  </p>
                  <Button onClick={resetFilters}>Reset Filters</Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      <PublicFooter />
      <ScrollToTop />
      <WhatsAppButton />
      
      {/* Floating Compare & Favorites Buttons */}
      {compareList.length > 0 && (
        <button
          onClick={() => navigate('/compare')}
          className="fixed bottom-24 left-8 z-40 flex items-center space-x-2 bg-red-600 text-white px-4 py-3 rounded-full shadow-lg hover:bg-red-700 transition-all"
        >
          <GitCompare className="h-5 w-5" />
          <span className="font-semibold">Compare ({compareList.length})</span>
        </button>
      )}
      
      {favorites.length > 0 && (
        <button
          onClick={() => navigate('/favorites')}
          className="fixed bottom-40 left-8 z-40 flex items-center space-x-2 bg-red-500 text-white px-4 py-3 rounded-full shadow-lg hover:bg-red-600 transition-all"
        >
          <Heart className="h-5 w-5 fill-current" />
          <span className="font-semibold">Favorites ({favorites.length})</span>
        </button>
      )}
    </div>
  );
};

export default VehicleListPage;
